import { useNavigate, useParams } from "react-router-dom";
import { useLocales, useUser } from "../../hooks";
import { useGetOneByIdQuery, useUpdateMutation } from "../../features/api/Api";
import { useAppSelector } from "../../app/hooks";
import { selectCustomerAddressLocation } from "../../features/customer/customerSlice";
import Swal from "sweetalert2";
import {
  isExists,
  locale,
  locationTypeEnum,
  nationOptions,
  onlyNumberKey,
  setEditAddress,
} from "../../utils";
import { saveHandler } from "../../handlers";
import { Form, Formik } from "formik";
import { setAddressById } from "../applications/addresses/common/setAddressById";
import { addressValidation } from "../applications/addresses/common/validations";
import { CircularProgress, Grid } from "@mui/material";
import {
  AutocompleteDropDown,
  CheckboxInput,
  FormFooter,
  ReferenceDropDown,
  TextInput,
} from "../../components";

const CustomerAddressDetail = () => {
  const { user } = useUser();
  const { translate } = useLocales();
  const history = useNavigate();
  const { customerId } = useParams();
  const { addressId } = useParams();
  const [update] = useUpdateMutation();
  const addressLocations = useAppSelector(selectCustomerAddressLocation);

  const { data: address } = useGetOneByIdQuery<any>({
    resource: `fixity/customer/address`,
    id: addressId,
  });

  function updateMethod(values: any, { setSubmitting }: any) {
    update({
      resource: `fixity/customer/address`,
      params: values,
    })
      .unwrap()
      .then(() => {
        Swal.fire(translate("actions.saved"), "", "success");
        history(`/applications/${customerId}/address`);
      })
      .catch((err) => {
        const error = err.data?.errors?.[0];
        Swal.fire(translate("actions.error"), error, "error");
        setSubmitting(false);
      });
  }

  function submitHandler(values: any, { setSubmitting }: any) {
    const customAddress = setEditAddress(values);
    saveHandler(customAddress, { setSubmitting }, updateMethod);
  }

  return (
    <>
      {isExists(address?.data) ? (
        <Formik
          initialValues={{
            ...address.data,
            isForeign: address.data.nationalityId === 2 ? true : false,
            ...setAddressById(address.data.fullAddressList),
            creatorEmail: user.email,
            ...(isExists(address?.data?.fullAddressList) && {
              fullAddres: {
                city: address?.data?.fullAddressList[1].name,
                town: address?.data?.fullAddressList[2].name,
                townShip: address?.data?.fullAddressList[3].name,
                village: address?.data?.fullAddressList[4].name,
                district: address?.data?.fullAddressList[5].name,
                street: address?.data?.fullAddressList[6].name,
                building: address?.data?.fullAddressList[7].name,
                door: address?.data?.fullAddressList[8].name,
              },
            }),
            nationalityId:
              !address.data.isForeign && address.data.nationalityId === 2
                ? address.data?.fullAddressList?.find(
                    (item: any) =>
                      item.locationTypeId === locationTypeEnum.country
                  )?.id
                : address.data.nationalityId,
          }}
          onSubmit={submitHandler}
          validationSchema={addressValidation(
            addressLocations,
            address?.data?.customerDetail?.eConversion || false
          )}
        >
          {}
          {(props) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item md={12}>
                  <CheckboxInput
                    name="isDefaultAddress"
                    label={translate(
                      "resources.customers.addresses.default_address"
                    )}
                  />
                  <CheckboxInput
                    name="isForeign"
                    label={translate(
                      "resources.customers.addresses.is_foreign"
                    )}
                    disabled
                  />
                </Grid>
                <Grid item md={6}>
                  <ReferenceDropDown
                    name="addressDetail.addressTypeId"
                    label={translate(
                      "resources.customers.addresses.address_type"
                    )}
                    reference="fixity/customer/addressType"
                    optionName="name"
                    disabled={true}
                  />
                </Grid>
                <Grid item md={3}>
                  <TextInput
                    name="addressDetail.uniqueAddressCode"
                    label={translate(
                      "resources.customers.addresses.address_code"
                    )}
                    type="number"
                    onKeyPressCapture={(event: any) => onlyNumberKey(event)}
                    disabled={true}
                  />
                </Grid>
                <Grid item md={3}>
                  <TextInput
                    name="addressDetail.bbkCode"
                    label={translate("resources.customers.addresses.bbk_code")}
                    type="number"
                    onKeyPressCapture={(event: any) => onlyNumberKey(event)}
                    disabled={true}
                  />
                </Grid>
                {/* <Grid item md={2}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#853EF8" }}
                >
                  {translate("resources.customers.addresses.get")}
                </Button>
              </Grid> */}
                <Grid item md={6}>
                  <TextInput
                    name="name"
                    label={translate(
                      "resources.customers.addresses.address_name"
                    )}
                    disabled={true}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextInput
                    name="detail"
                    label={translate(
                      "resources.customers.addresses.address_detail"
                    )}
                    disabled={true}
                  />
                </Grid>
                <Grid item md={6}>
                  {props.values.isForeign && (
                    <ReferenceDropDown
                      name="nationalityId"
                      label={translate(
                        "resources.customers.addresses.country_name"
                      )}
                      reference={`fixity/setting/addressLocation/address-location-nationality`}
                      optionName="name"
                      secondField={{ name: "countryName", value: "name" }}
                      disabled={true}
                    />
                  )}
                  {!props.values.isForeign && (
                    <AutocompleteDropDown
                      name="nationalityId"
                      label={translate(
                        "resources.customers.addresses.country_name"
                      )}
                      options={locale(
                        nationOptions,
                        "resources.customers.addresses."
                      )}
                      disabled={true}
                    />
                  )}
                </Grid>
                <Grid item md={6}>
                  {/* {props.values.isForeign && (
                    <AddressReferenceDropDown
                      name="city"
                      label={translate("resources.customers.addresses.city")}
                      reference="fixity/setting/addressLocation/get-foreign-city"
                      optionName="name"
                      query={{
                        parentNationalityId: props.values.nationalityId,
                      }}
                      optionId="turkTelekomId"
                      secondField={{
                        name: "address.addressLocationId",
                        value: "id",
                      }}
                      disabled={true}
                      skip={!isExists(props.values.nationalityId)}
                    />
                  )}
                  {!props.values.isForeign && (
                    <AddressReferenceDropDown
                      name="city"
                      label={translate("resources.customers.addresses.city")}
                      reference="fixity/setting/TurkTelekomAddress/get-cities/187cf1ae-023f-ed11-a5f4-005056a2cd35"
                      optionName="name"
                      optionId="turkTelekomId"
                      secondField={{
                        name: "address.addressLocationId",
                        value: "id",
                      }}
                      disabled={true}
                      skip={!isExists(props.values.nationalityId)}
                    />
                  )} */}

                  <TextInput
                    name="fullAddres.city"
                    label={translate("resources.customers.addresses.city")}
                    disabled={true}
                  />
                </Grid>
                {!props.values.isForeign && (
                  <>
                    <Grid item md={6}>
                      {/* {props.values?.city && (
                        // <AddressReferenceDropDown
                        //   name="town"
                        //   label={translate(
                        //     "resources.customers.addresses.town"
                        //   )}
                        //   reference={`fixity/setting/TurkTelekomAddress/get-towns-by-city-id`}
                        //   query={{ CityId: props.values.city.value }}
                        //   optionName="name"
                        //   optionId="turkTelekomId"
                        //   secondField={{
                        //     name: "addressDetail.addressLocationId",
                        //     value: "id",
                        //   }}
                        //   disabled={true}
                        // />
                        //  {props.values?.fullAddres?.city && (
                        
                      )} */}
                      {props.values?.fullAddres?.city && (
                        <TextInput
                          name="fullAddres.town"
                          label={translate(
                            "resources.customers.addresses.town"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      {/* {props.values?.town && (
                        <AddressReferenceDropDown
                          name="townShip"
                          label={translate(
                            "resources.customers.addresses.townShip"
                          )}
                          reference={`fixity/setting/TurkTelekomAddress/get-townships-by-town-id`}
                          query={{ TownId: props.values.town.value }}
                          optionName="name"
                          optionId="turkTelekomId"
                          secondField={{
                            name: "addressDetail.addressLocationId",
                            value: "id",
                          }}
                          disabled={true}
                        />
                      )} */}
                      {props.values?.fullAddres?.town && (
                        <TextInput
                          name="fullAddres.townShip"
                          label={translate(
                            "resources.customers.addresses.townShip"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      {/* {props.values?.townShip && (
                        <AddressReferenceDropDown
                          name="village"
                          label={translate(
                            "resources.customers.addresses.village"
                          )}
                          reference={`fixity/setting/TurkTelekomAddress/get-villages-by-township-id`}
                          query={{ TownShipId: props.values.townShip.value }}
                          optionName="name"
                          optionId="turkTelekomId"
                          secondField={{
                            name: "addressDetail.addressLocationId",
                            value: "id",
                          }}
                          disabled={true}
                        />
                      )} */}
                      {props.values?.fullAddres?.townShip && (
                        <TextInput
                          name="fullAddres.village"
                          label={translate(
                            "resources.customers.addresses.village"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      {/* {props.values?.village && (
                        <AddressReferenceDropDown
                          name="district"
                          label={translate(
                            "resources.customers.addresses.district"
                          )}
                          reference={`fixity/setting/TurkTelekomAddress/get-districts-by-village-id`}
                          query={{ VillageId: props.values.village.value }}
                          optionName="name"
                          optionId="turkTelekomId"
                          secondField={{
                            name: "addressDetail.addressLocationId",
                            value: "id",
                          }}
                          disabled={true}
                        />
                      )} */}
                      {props.values?.fullAddres?.village && (
                        <TextInput
                          name="fullAddres.district"
                          label={translate(
                            "resources.customers.addresses.district"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      {/* {props.values?.district && (
                        <AddressReferenceDropDown
                          name="street"
                          label={translate(
                            "resources.customers.addresses.street"
                          )}
                          reference={`fixity/setting/TurkTelekomAddress/get-streets-by-district-id`}
                          query={{ DistrictId: props.values.district.value }}
                          optionName="name"
                          optionId="turkTelekomId"
                          secondField={{
                            name: "addressDetail.addressLocationId",
                            value: "id",
                          }}
                          disabled={true}
                        />
                      )} */}
                      {props.values?.fullAddres?.district && (
                        <TextInput
                          name="fullAddres.street"
                          label={translate(
                            "resources.customers.addresses.street"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      {/* {props.values?.street && (
                        <AddressReferenceDropDown
                          name="building"
                          label={translate(
                            "resources.customers.addresses.building"
                          )}
                          reference={`fixity/setting/TurkTelekomAddress/get-building-numbers-by-street-id`}
                          query={{ StreetId: props.values.street.value }}
                          optionName="name"
                          optionId="turkTelekomId"
                          secondField={{
                            name: "addressDetail.addressLocationId",
                            value: "id",
                          }}
                          disabled={true}
                        />
                      )} */}
                      {props.values?.fullAddres?.street && (
                        <TextInput
                          name="fullAddres.building"
                          label={translate(
                            "resources.customers.addresses.building"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      {/* {props?.values?.building && (
                        <AddressReferenceDropDown
                          name="door"
                          label={translate(
                            "resources.customers.addresses.door"
                          )}
                          reference={`fixity/setting/TurkTelekomAddress/get-door-numbers-by-building-id`}
                          query={{ BuildingId: props.values.building.value }}
                          optionName="name"
                          optionId="turkTelekomId"
                          secondField={{
                            name: "addressDetail.addressLocationId",
                            value: "id",
                          }}
                          disabled={true}
                        />
                      )} */}
                      {props.values?.fullAddres?.building && (
                        <TextInput
                          name="fullAddres.door"
                          label={translate(
                            "resources.customers.addresses.door"
                          )}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item md={6}>
                      <TextInput
                        name="addressDetail.postalCode"
                        label={translate(
                          "resources.customers.addresses.postal_code"
                        )}
                        disabled={true}
                      />
                    </Grid>
                    {/* <Grid item md={6}>
                      <TextInput
                        name="addressDetail.vsatId"
                        label={translate(
                          "resources.customers.addresses.vsatId"
                        )}
                        disabled={true}
                      />
                    </Grid> */}
                    {/* <Grid item md={6}></Grid> */}
                    <Grid item md={6}>
                      <TextInput
                        name="addressDetail.latitude"
                        label={translate(
                          "resources.customers.addresses.latitude"
                        )}
                        disabled={true}
                      />
                    </Grid>
                    <Grid item md={6}>
                      <TextInput
                        name="addressDetail.longitude"
                        label={translate(
                          "resources.customers.addresses.longitude"
                        )}
                        disabled={true}
                      />
                    </Grid>
                    {/* {props.values?.city?.label && props.values?.town?.label && (
                    <LatLongInputs
                      city={props.values.city.label}
                      town={props.values.town.label}
                    />
                  )} */}
                  </>
                )}
              </Grid>

              <FormFooter>
                <Grid container justifyContent="center" spacing={3}>
                  <Grid item>
                    {/*  <DeleteButton
                      disabled={props.isSubmitting}
                      resource="fixity/customer/address"
                      redirectPath={`/applications/${customerId}/address`}
                    /> */}
                  </Grid>
                  <Grid item>
                    {/* <EditButton disabled={props.isSubmitting} /> */}
                  </Grid>
                </Grid>
              </FormFooter>
            </Form>
          )}
        </Formik>
      ) : (
        <CircularProgress />
      )}
    </>
  );
};

export default CustomerAddressDetail;
